// ============================================================================
// titan.js — the Titan gauge (Musou replacement) and TITANFALL, plus the
// shockwave system shared by the combo finisher wave and the Titanfall pulses.
// A shockwave is an expanding ring that knocks back everything it sweeps past.
// ============================================================================

import {
  TITAN_MAX, TITANFALL_DURATION, TITANFALL_RADIUS, TITANFALL_TICK,
  TITANFALL_DMG, TITANFALL_KNOCK, TEAM_PLAYER, ST_DEAD, ST_BLOCK,
  RE_KNOCK,
} from '../core/constants.js';
import { applyReaction, applyDeathImpulse } from './reactions.js';
import { EV } from '../core/events.js';

const MAX_SHOCKWAVES = 32;

export function addTitan(world, amount) {
  const p = world.player;
  if (p.titan >= TITAN_MAX) return;
  p.titan = Math.min(TITAN_MAX, p.titan + amount);
  if (p.titan >= TITAN_MAX && !p.titanReady) {
    p.titanReady = true;
    world.events.emit(EV.TITAN_READY, {});
  }
}

export function canTitanfall(world) {
  const p = world.player;
  return p.titanReady && p.titan >= TITAN_MAX && p.state !== 'titanfall' && p.state !== 'dead';
}

export function startTitanfall(world) {
  if (!canTitanfall(world)) return false;
  const p = world.player;
  p.state = 'titanfall';
  p.titanfallT = TITANFALL_DURATION;
  p.titan = 0;
  p.titanReady = false;
  p.invuln = TITANFALL_DURATION + 0.2;
  p._titanPulse = 0;
  world.shake = Math.min(1.4, world.shake + 0.9);
  // opening eruption — a huge black wave
  spawnShockwave(world, p.x, p.z, TITANFALL_DMG * 1.5, TITANFALL_KNOCK * 1.4, TITANFALL_RADIUS + 6, 34, TEAM_PLAYER, 1);
  world.events.emit(EV.TITANFALL, {});
  return true;
}

export function updateTitanfall(world, dt) {
  const p = world.player;
  if (p.state !== 'titanfall') return;
  p.titanfallT -= dt;
  p._titanPulse -= dt;
  if (p._titanPulse <= 0) {
    p._titanPulse = TITANFALL_TICK;
    spawnShockwave(world, p.x, p.z, TITANFALL_DMG, TITANFALL_KNOCK, TITANFALL_RADIUS, 40, TEAM_PLAYER, 1);
    world.shake = Math.min(1.4, world.shake + 0.12);
  }
  if (p.titanfallT <= 0) {
    p.state = 'idle';
    p.titanfallT = 0;
  }
}

// ---- shockwaves --------------------------------------------------------

export function spawnShockwave(world, x, z, dmg, knock, rMax, speed, team, kind) {
  const arr = world.shockwaves;
  // reuse a dead slot if possible (keeps allocations off the hot path)
  let w = null;
  for (let i = 0; i < arr.length; i++) if (!arr[i].active) { w = arr[i]; break; }
  if (!w) {
    if (arr.length >= MAX_SHOCKWAVES) return null;
    w = {};
    arr.push(w);
  }
  w.active = true;
  w.x = x; w.z = z; w.r = 0.4; w.rMax = rMax; w.speed = speed; w.band = 2.2;
  w.dmg = dmg; w.knock = knock; w.team = team; w.kind = kind || 0;
  w.token = ++world.nextToken;
  w.life = 0; w.maxLife = rMax / speed;
  return w;
}

export function updateShockwaves(world, dt) {
  const arr = world.shockwaves;
  const units = world.units;
  for (let i = 0; i < arr.length; i++) {
    const w = arr[i];
    if (!w.active) continue;
    const prevR = w.r;
    w.r += w.speed * dt;
    w.life += dt;
    // apply to units the leading edge sweeps over this step
    const outer = w.r, inner = Math.max(0, prevR - w.band);
    world.hash.forEachInRadius(w.x, w.z, outer + 1, (slot) => {
      const u = units.get(slot);
      if (!u._active || u.team === w.team || u.state === ST_DEAD) return;
      if (u.hitToken === w.token) return;
      const dx = u.x - w.x, dz = u.z - w.z;
      const d = Math.hypot(dx, dz);
      if (d > outer || d < inner) return;
      u.hitToken = w.token;
      u.hp -= w.dmg;
      u.flash = 1;
      for (let k = 0; k < 3; k++) {
        const a = world.rng() * 6.28, sp = 3 + world.rng() * 6;
        world.spawnSpark(u.x, 1.0 + world.rng(), u.z, Math.cos(a) * sp, 3 + world.rng() * 5, Math.sin(a) * sp,
          0.35, 0.7, w.kind === 1 ? 0 : 3 /* titanfall ember / black */);
      }
      if (u.hp <= 0) {
        world.killUnit(u, true);
        applyDeathImpulse(u, w.x, w.z, w.knock + 8);
      } else {
        applyReaction(world, u, RE_KNOCK, w.x, w.z, w.knock, 4);
      }
      world.events.emit(EV.HIT, { x: u.x, z: u.z, damage: w.dmg, kill: u.hp <= 0, blocked: false });
    });
    if (w.r >= w.rMax) w.active = false;
  }
}
